import type { ChartSymbolIdentity } from '@/lib/tradingview/chart-symbol-identity'
import { qualifyChartSymbol } from '@/lib/tradingview/chart-symbol-identity'

export interface ChartSeriesSubscriptionContext {
  identity: ChartSymbolIdentity
  timeframeSeconds: number
}

const DEFAULT_GRACE_MS = 10_000

export function getChartSeriesSubscriptionKey(context: ChartSeriesSubscriptionContext): string {
  return `${qualifyChartSymbol(context.identity)}|${context.timeframeSeconds}`
}

export class ChartSeriesSubscriptionManager {
  private readonly contexts = new Map<string, ChartSeriesSubscriptionContext>()
  private readonly refCounts = new Map<string, number>()
  private readonly cleanupTimers = new Map<string, ReturnType<typeof setTimeout>>()
  private readonly listeners = new Set<() => void>()

  constructor(private readonly graceMs = DEFAULT_GRACE_MS) {}

  acquire(context: ChartSeriesSubscriptionContext): string {
    const key = getChartSeriesSubscriptionKey(context)
    const cleanupTimer = this.cleanupTimers.get(key)
    if (cleanupTimer) {
      clearTimeout(cleanupTimer)
      this.cleanupTimers.delete(key)
    }
    this.contexts.set(key, context)
    this.refCounts.set(key, (this.refCounts.get(key) ?? 0) + 1)
    this.emit()
    return key
  }

  release(key: string): void {
    const nextCount = (this.refCounts.get(key) ?? 0) - 1
    if (nextCount > 0) {
      this.refCounts.set(key, nextCount)
      return
    }
    this.refCounts.delete(key)
    if (this.cleanupTimers.has(key)) return
    this.cleanupTimers.set(
      key,
      setTimeout(() => {
        this.cleanupTimers.delete(key)
        if (this.refCounts.has(key)) return
        this.contexts.delete(key)
        this.emit()
      }, this.graceMs)
    )
  }

  getActiveContexts(): ChartSeriesSubscriptionContext[] {
    return [...this.contexts.values()]
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  destroy(): void {
    this.cleanupTimers.forEach(clearTimeout)
    this.cleanupTimers.clear()
    this.refCounts.clear()
    this.contexts.clear()
    this.emit()
    this.listeners.clear()
  }

  private emit(): void {
    this.listeners.forEach((listener) => listener())
  }
}
